import styled, { keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../../../components/AppShell/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

export const Container = styled.div`
  padding-bottom: 2rem;
`;

/* A titled block of the screen: small uppercase label + card (same as /settings) */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }
`;

/* Green status block, like the other success/PLUS highlights */
export const SuccessCard = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(87, 242, 139, 0.18) 0%, rgba(87, 242, 139, 0) 60%),
    var(--color-neutral-dark);
  border-radius: 1rem;
  box-shadow: inset 0 0 0 1px rgba(87, 242, 139, 0.25);

  @media ${MOBILE} {
    padding: 20px;
  }

  .check {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    color: var(--color-neutral-darkness);
    background: var(--color-brand-plus);
    border-radius: 100%;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  h3 {
    font-family: Poppins, sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.5rem;
    color: var(--color-neutral-white);
  }

  p {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }
`;

const shimmer = keyframes`
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
`;

/* The Keepo's address with copy/open, like the link line of the Keepo dashboard, plus the custom
   domain: shown as the main address when connected, offered in the footer otherwise */
export const LinkCard = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    padding: 0.75rem 0.75rem 0.75rem 20px;
  }

  /* Placeholder with the row's shape while the custom domain is loading */
  .skeleton {
    min-height: 60px;

    span {
      display: block;
      height: 14px;
      background: var(--color-neutral-base);
      border-radius: 100px;
      animation: ${shimmer} 1.2s ease-in-out infinite;
    }

    .bar {
      flex: 1;
      max-width: 220px;
      margin-right: auto;
    }

    .pill {
      width: 84px;
      height: 36px;
    }
  }

  /* One address per row (custom domain first, when connected) */
  .row + .row {
    border-top: 1px solid var(--color-neutral-base);
  }

  .address {
    display: flex;
    flex: 1;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
  }

  .url {
    overflow: hidden;
    font-family: Inter, sans-serif;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .action {
    display: inline-flex;
    flex-shrink: 0;
    gap: 0.375rem;
    align-items: center;
    height: 36px;
    padding: 0 0.875rem;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    border: 1px solid var(--color-neutral-light);
    border-radius: 100px;
    transition: background 0.15s;

    svg {
      width: 14px;
      height: 14px;
    }

    &:hover {
      background: var(--color-neutral-base);
    }

    &.copied {
      color: var(--color-brand-plus);
      border-color: var(--color-brand-plus);
    }
  }

  /* Custom domain offer: a full-width row under a divider */
  .domain-offer {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    width: 100%;
    padding: 0.875rem 20px;
    text-align: left;
    border-top: 1px solid var(--color-neutral-base);
    transition: background-color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.03);
    }

    > svg {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      color: var(--color-neutral-150);
    }

    .text {
      display: flex;
      flex: 1;
      flex-wrap: wrap;
      gap: 0.25rem 0.5rem;
      align-items: center;
      min-width: 0;
      font-family: Inter, sans-serif;
      font-size: 0.8125rem;
      line-height: 1.125rem;
      color: var(--color-neutral-150);

      strong {
        color: var(--color-neutral-white);
      }
    }

    .side {
      display: flex;
      flex-shrink: 0;
      gap: 0.75rem;
      align-items: center;
    }

    .cta {
      font-family: Inter, sans-serif;
      font-size: 0.8125rem;
      font-weight: 700;
      color: var(--color-brand-plus);
    }
  }

  @media ${MOBILE} {
    /* Phones: one line per address, with icon-only round buttons on the right */
    .row {
      flex-wrap: nowrap;
      padding: 0.75rem 0.75rem 0.75rem 20px;
    }

    .action {
      justify-content: center;
      width: 36px;
      padding: 0;

      .label {
        display: none;
      }
    }

    .skeleton .pill {
      width: 36px;
    }

    /* The offer's title and example stack; the PLUS tag and the CTA stay side by side on the right */
    .domain-offer {
      /* The globe is decorative: give its room to the text */
      > svg {
        display: none;
      }

      .text {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.125rem;

        > * {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        span {
          font-size: 0.75rem;
        }
      }

      .side {
        gap: 0.5rem;
      }
    }
  }
`;

/* One grouped card with a row per next step, like /settings' advanced options */
export const List = styled.ul`
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  li + li {
    border-top: 1px solid var(--color-neutral-base);
  }

  button {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    width: 100%;
    padding: 1rem 20px;
    text-align: left;
    transition: background-color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.03);
    }
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .title {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .description {
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .chevron {
    display: flex;
    flex-shrink: 0;
    color: var(--color-neutral-150);
  }
`;
